import React, {useCallback, useState, useRef, useEffect} from 'react'

interface MultilineEditProps {
  value: string
  setValue: (value: string) => void
  placeholder?: string
}

const MultilineEdit: React.FC<MultilineEditProps> = ({
  value,
  setValue,
  placeholder = 'Click to add notes...',
}) => {
  const [editingValue, setEditingValue] = useState(value)
  const [isFocused, setIsFocused] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    setEditingValue(value)
  }, [value])

  useEffect(() => {
    if (textareaRef.current) {
      adjustTextareaHeight(textareaRef.current)
    }
  }, [editingValue])

  const onChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEditingValue(event.target.value)
  }

  const onKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Escape') {
      event.currentTarget.blur()
    }
  }

  const onFocus = () => {
    setIsFocused(true)
  }

  const onBlur = (event: React.FocusEvent<HTMLTextAreaElement>) => {
    setIsFocused(false)
    setValue(event.target.value)
  }

  const adjustTextareaHeight = (target: HTMLTextAreaElement) => {
    target.style.height = 'auto'
    target.style.height = `${Math.max(30, Math.min(target.scrollHeight, 60))}px`
  }

  return (
    <div className={`notes-editor-wrapper ${isFocused ? 'focused' : ''}`}>
      <textarea
        className={`form-control form-control-solid notes-textarea ${!editingValue ? 'empty' : ''}`}
        value={editingValue}
        onFocus={onFocus}
        onBlur={onBlur}
        onChange={onChange}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        ref={textareaRef}
        style={{
          minHeight: '30px',
          maxHeight: '60px',
          transition: 'all 0.2s ease',
          resize: 'vertical',
          borderRadius: '0.475rem',
          padding: '0.45rem 0.75rem',
          lineHeight: '1.3',
        }}
      />
      {isFocused && <div className='mt-1 text-end text-muted small'>Press Esc to cancel</div>}
    </div>
  )
}

export default MultilineEdit
